/**
 * Pure view-model: turn the flat submission_files set (live + superseded, each
 * carrying replaced_by_file_id) into per-document groups with version chains.
 *
 * A "document" is a live file (superseded_at === null) with no successor. Its
 * versions are that file plus every file that chains into it via
 * replaced_by_file_id (transitively; a single file may replace several).
 * Legacy superseded rows with no chain attach to the sole live group, or — when
 * nothing is live — the newest file heads a history-only group.
 *
 * Dependency-free so it is trivially unit-testable.
 */
export type DocVersionFile = {
  id: string;
  file_name: string;
  size_bytes: number | null;
  expires_at: string | null;
  superseded_at: string | null;
  superseded_status?: string | null;
  replaced_by_file_id: string | null;
  created_at: string;
  download_url?: string | null;
};

export type DocumentVersion = DocVersionFile & {
  state: 'current' | 'replaced';
  /** 1-based chronological version number within the group (oldest = 1). */
  version: number;
};

export type DocumentGroup = {
  current: DocVersionFile;
  versions: DocumentVersion[];
};

/**
 * Whether a question's file area renders the read-only DocumentTable (version
 * history) instead of the editable FilesEditor. Only document_upload questions
 * with at least one live document ever use the table, and only once the vendor
 * can no longer manage files directly: the answer is locked (under review,
 * approved, not applicable), or we are in the post-submit view where
 * replacements flow through the comment composer. Pre-submit the vendor keeps
 * the editor so uploads can still be removed or replaced.
 */
export function showDocumentTable(opts: {
  questionType: string;
  hasDocuments: boolean;
  locked: boolean;
  postSubmit: boolean;
}): boolean {
  return (
    opts.questionType === 'document_upload' && opts.hasDocuments && (opts.locked || opts.postSubmit)
  );
}

function chronological(a: DocVersionFile, b: DocVersionFile): number {
  return a.created_at === b.created_at ? 0 : a.created_at < b.created_at ? -1 : 1;
}

export function buildDocumentGroups(files: DocVersionFile[]): DocumentGroup[] {
  if (files.length === 0) return [];

  const byId = new Map(files.map((file) => [file.id, file]));

  // Predecessors: for each file id, the files that name it as their successor.
  const predecessors = new Map<string, DocVersionFile[]>();
  for (const file of files) {
    if (file.replaced_by_file_id && byId.has(file.replaced_by_file_id)) {
      const list = predecessors.get(file.replaced_by_file_id) ?? [];
      list.push(file);
      predecessors.set(file.replaced_by_file_id, list);
    }
  }

  const live = files.filter((file) => file.superseded_at === null);
  const heads =
    live.length > 0 ? [...live].sort(chronological) : [[...files].sort(chronological).at(-1)!];

  const claimed = new Set<string>();
  const collectChain = (head: DocVersionFile): DocVersionFile[] => {
    const acc: DocVersionFile[] = [];
    const walk = (file: DocVersionFile) => {
      if (claimed.has(file.id)) return;
      claimed.add(file.id);
      acc.push(file);
      for (const predecessor of predecessors.get(file.id) ?? []) walk(predecessor);
    };
    walk(head);
    return acc;
  };

  const groups = heads.map((head) => ({ current: head, files: collectChain(head) }));

  // Legacy superseded rows with no chain: attach to the first group.
  const orphans = files.filter((file) => !claimed.has(file.id));
  if (orphans.length > 0 && groups.length > 0) {
    groups[0]!.files.push(...orphans);
  }

  return groups.map((group) => {
    const ordered = [...group.files].sort(chronological); // oldest → newest
    const versions: DocumentVersion[] = ordered.map((file, index) => ({
      ...file,
      state: file.superseded_at === null ? 'current' : 'replaced',
      version: index + 1,
    }));
    versions.reverse(); // display newest-first
    return { current: group.current, versions };
  });
}

/**
 * True when any live (non-superseded) file has an expiry date in the past —
 * the condition that opens the renewal door on an approved document question
 * (vendor may reply with a replacement; mirrors the guard in api me.ts).
 */
export function hasExpiredLiveFile(files: DocVersionFile[], now: Date): boolean {
  return files.some(
    (f) => f.superseded_at === null && f.expires_at != null && new Date(f.expires_at) < now,
  );
}
